import { useState } from 'react';
import { showToast } from 'Toast';
import {
    SAVED_FLASH_MS,
    VALIDATION_TOAST_MS,
    entityMetadataApi,
    entityMetadataFromForm,
    messageFrom,
    reportSaveFailure,
    type PublishMeta,
} from 'ContentItemEditor';
import { playlistEditorApi } from './playlistEditorApi';
import { fromForm } from './formFieldConversions';
import { type PlaylistFormValues, type PlaylistRecord } from './PlaylistEditor.types';

// Tells a failed metadata save apart from a playlist that has none to save.
const METADATA_SAVE_FAILED = Symbol('metadata save failed');

// The SEO metadata saves first, on its own request. The playlist save runs only if it succeeded.
type Options = { record: PlaylistRecord; onSaved: (updated: PlaylistRecord) => void };

export function usePlaylistSave({ record, onSaved }: Options) {
    const [updatePlaylist] = playlistEditorApi.useUpdatePlaylistMutation();
    const [updateEntityMetadata] = entityMetadataApi.useUpdateEntityMetadataMutation();
    const [saving, setSaving] = useState(false);
    const [justSaved, setJustSaved] = useState(false);
    const [staleVersion, setStaleVersion] = useState<string | null>(null);

    // Returns what the server stored, so the new baseline holds the saved SEO fields. undefined
    // means there is no metadata; FAILED means the playlist save must not run.
    async function saveEntityMetadata(values: PlaylistFormValues) {
        const entityMetadata = entityMetadataFromForm(values, record);
        if (!entityMetadata) return undefined;

        try {
            return await updateEntityMetadata({ record: entityMetadata }).unwrap();
        } catch (error) {
            // The playlist save never runs, so nothing was written. EntityMetadata requires a
            // title, which is the failure an admin is most likely to hit here.
            showToast({
                message: 'Nothing was saved',
                description: messageFrom(error, 'The SEO metadata is invalid, so the playlist was not saved.'),
                type: 'danger',
                timeout: VALIDATION_TOAST_MS,
            });
            return METADATA_SAVE_FAILED;
        }
    }

    async function save(values: PlaylistFormValues, meta: PublishMeta) {
        setSaving(true);
        let metadataSaved = false;

        try {
            const savedMetadata = await saveEntityMetadata(values);
            if (savedMetadata === METADATA_SAVE_FAILED) return;

            metadataSaved = !!savedMetadata;

            const saved = fromForm(values, record);
            const savedFields = await updatePlaylist({ record: saved, meta }).unwrap();

            // The baseline is what was sent, merged with the response. The next save needs the
            // new updated_at, and Related Playlists follows family_playlist_titles.
            onSaved({
                ...saved,
                ...(savedMetadata ? { entity_metadata: savedMetadata } : {}),
                ...savedFields,
            });

            setJustSaved(true);
            window.setTimeout(() => setJustSaved(false), SAVED_FLASH_MS);
        } catch (error) {
            // The server refuses an exam change on a locked playlist; the page does not block it.
            reportSaveFailure(error, setStaleVersion, { itemLabel: 'playlist', metadataSaved });
        } finally {
            setSaving(false);
        }
    }

    return { save, saving, justSaved, staleVersion, dismissStaleVersion: () => setStaleVersion(null) };
}
